// Keep the product gallery's thumbnail rail showing the photo that is selected.
//
// The rail (`.slideshow-controls__thumbnails-container`) shows about 7
// thumbnails of however many the product has. It is NOT a wheel target: its CSS
// is `overflow: hidden` on purpose (see snippets/slideshow-controls.liquid), so
// the wheel scrolls the page over it exactly as it does anywhere else, and the
// rail and the page never compete for a gesture. Everything that moves the rail
// is programmatic, which `overflow: hidden` still allows.
//
// Which leaves this file with one job - after three rounds of the merchant
// scrolling it and saying "too fast", "sticky / stuck", "breadcrumb moves
// quickly", the honest fix was to stop interpreting the wheel at all and make
// the rail follow the gallery properly instead.

// The hook is the slideshow's own bubbling `slideshow:select`, not the rail's
// markup: the controls are re-rendered rather than edited in place, so a new
// thumbnail arrives already carrying aria-selected="true" and an attribute
// observer never sees a mutation to react to.
//
// Only acts when the thumbnail is actually outside the rail, so it never fights
// slideshow.js's own centring on the occasions that one does fire.

const RAIL_SELECTOR = '.slideshow-controls__thumbnails-container';
const THUMB_SELECTOR = '.slideshow-controls__thumbnail';

/** @param {Element | undefined} thumb */
function revealThumbnail(thumb) {
  if (!(thumb instanceof HTMLElement)) return;
  const rail = thumb.closest(RAIL_SELECTOR);
  if (!(rail instanceof HTMLElement)) return;
  if (rail.scrollHeight - rail.clientHeight <= 1) return;

  const top = thumb.offsetTop - rail.offsetTop;
  const bottom = top + thumb.offsetHeight;
  if (top >= rail.scrollTop && bottom <= rail.scrollTop + rail.clientHeight) return;

  const centred = top - (rail.clientHeight - thumb.offsetHeight) / 2;
  const target = Math.max(0, Math.min(centred, rail.scrollHeight - rail.clientHeight));

  // A direct assignment, not scrollTo({behavior:'smooth'}): a SMOOTH scroll on
  // this rail is silently ignored - it never moves, not by a pixel, measured
  // from a clean page with nothing else running. That is why the rail never
  // followed the gallery on its own: slideshow.js's #centerSelectedThumbnail
  // asks for exactly that smooth scroll (observed asking for top: 609.69 while
  // the rail stayed at 0), so the shopper was left with photo 12 selected and
  // thumbnail 1 showing. Instant assignment works every time.
  //
  // Nor requestAnimationFrame: rAF does not run in a background tab, so a
  // reveal deferred to it is lost on any page the shopper is not looking at -
  // including, confusingly, a tab being driven for testing.
  rail.scrollTop = target;
}

/** @param {Element} scope @param {number} index */
function revealIndex(scope, index) {
  for (const rail of scope.querySelectorAll(RAIL_SELECTOR)) {
    revealThumbnail(rail.querySelectorAll(THUMB_SELECTOR)[index]);
  }
}

document.addEventListener('slideshow:select', (event) => {
  const target = event.target;
  const index = /** @type {any} */ (event).detail?.index;
  if (!(target instanceof Element) || typeof index !== 'number') return;
  const scope = target.closest('slideshow-component') ?? target;
  revealIndex(scope, index);
});

// On load the selected photo is already chosen - a variant's own hero - and on a
// gallery sorted by colour that can sit well down the rail.
function revealSelected() {
  for (const rail of document.querySelectorAll(RAIL_SELECTOR)) {
    const selected = rail.querySelector('[aria-selected="true"]');
    if (selected) revealThumbnail(selected);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', revealSelected, { once: true });
} else {
  revealSelected();
}
